import React, { Fragment, useCallback, useRef, useState } from 'react';
import {
  CenteredContainer,
  ExpandableTableHeaderItem,
  NoDataText,
  StyledTable,
  TableBody,
  TableBodyItem,
  TableBodyRow,
  TableFooter,
  TableHeader,
  TableHeaderItem,
  TableHeaderRow,
  TableWrapper,
} from './Table.styles';
import { ReactComponent as ArrowUp } from '../../assets/icons/arrow-up.svg';
import { ReactComponent as ArrowDown } from '../../assets/icons/arrow-down.svg';
import Pagination from './Pagination/Pagination';
import Spinner from '../Spinner/Spinner';
import PerPageSelect from './PerPageSelect/PerPageSelect';

export const ORDER = {
  ASC: 'ASC',
  DESC: 'DESC',
};

const Table = ({
  children,
  currentPage,
  totalCount,
  perPage,
  setPerPage,
  setPage,
  headerInformation,
  sortOptions,
  setSortOptions,
  emptyDataText,
  expandable,
  dataLoading,
  fullHeight,
  showPerPage,
  hideFooter,
}) => {
  const bodyRef = useRef();
  const [scrollOnBottom, setScrollOnBottom] = useState(false);

  const handleScroll = () => {
    const container = bodyRef.current;
    if (!container) return;
    const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 1;
    setScrollOnBottom(isAtBottom);
  };

  const onSort = key => {
    if (sortOptions.order === ORDER.DESC && !sortOptions.sortBy) {
      setSortOptions({ ...sortOptions, sortBy: key });
    } else if (key !== sortOptions.sortBy) {
      setSortOptions({ sortBy: key, order: ORDER.ASC });
    } else if (sortOptions.order === ORDER.DESC && sortOptions.sortBy) {
      setSortOptions({ sortBy: key, order: ORDER.ASC });
    } else {
      setSortOptions({ sortBy: '', order: ORDER.DESC });
    }
  };

  const renderTableBody = useCallback(() => {
    if (dataLoading) {
      return (
        <TableBodyRow $expandedRow>
          <TableBodyItem>
            <CenteredContainer>
              <Spinner width={100} height={100} />
            </CenteredContainer>
          </TableBodyItem>
        </TableBodyRow>
      );
    }

    if (!children || !children.length) {
      return (
        <TableBodyRow $expandedRow>
          <TableBodyItem>
            <CenteredContainer>
              <NoDataText>{emptyDataText}</NoDataText>
            </CenteredContainer>
          </TableBodyItem>
        </TableBodyRow>
      );
    }

    return children.map((child, index) => {
      if (expandable) {
        return <Fragment key={index}>{child}</Fragment>;
      }
      const onClick = child?.props?.onDetailsClicked;
      return (
        <TableBodyRow key={index} $pointer={!!onClick} onClick={() => (onClick ? onClick() : null)}>
          {child}
        </TableBodyRow>
      );
    });
  }, [children, dataLoading, emptyDataText, expandable]);

  return (
    <TableWrapper $fullHeight={fullHeight}>
      <StyledTable>
        <TableHeader>
          <TableHeaderRow>
            {expandable && <ExpandableTableHeaderItem />}
            {headerInformation.map((item, index) => {
              return (
                <TableHeaderItem
                  key={index}
                  $sortable={item.key}
                  $sorted={item.key && item.key === sortOptions?.sortBy}
                  onClick={() => {
                    item.key && item.sortable && onSort(item.key);
                  }}
                  $actionWidth={item.actionWidth}
                  $isAction={item.type === 'action'}
                  $fixedWidth={item.fixedWidth}>
                  {item.label}
                  {item.key && item.sortable && (
                    <>
                      {sortOptions.order === ORDER.ASC && item.key === sortOptions.sortBy ? (
                        <ArrowUp fill="#1672BA" />
                      ) : (
                        <ArrowDown fill="#1672BA" />
                      )}
                    </>
                  )}
                </TableHeaderItem>
              );
            })}
          </TableHeaderRow>
        </TableHeader>
        <TableBody
          ref={bodyRef}
          onScroll={handleScroll}
          $showFade={totalCount > perPage && perPage > 10 && !scrollOnBottom}>
          {renderTableBody()}
        </TableBody>
      </StyledTable>
      {!hideFooter && (
        <TableFooter $hasPerPageSelect={showPerPage}>
          {!dataLoading && totalCount > perPage && (
            <>
              {showPerPage && (
                <PerPageSelect
                  perPage={perPage}
                  setPerPage={value => {
                    setScrollOnBottom(false);
                    setPerPage(value);
                  }}
                />
              )}
              <Pagination
                currentPage={currentPage}
                totalCount={totalCount}
                perPage={perPage}
                onPageChange={page => setPage(page)}
              />
            </>
          )}
        </TableFooter>
      )}
    </TableWrapper>
  );
};

export default Table;
